import React, { useState, useEffect, useCallback } from "react";
import * as XLSX from "xlsx";

export default function NominaPagos({ usuario, onBackToMenu }) {
  // Permisos: Super Admin, Directivos, Talento Humano y SST pueden cargar nómina
  const cargoUpper = String(usuario?.cargo || "").toUpperCase();
  const esRRHH = 
    Boolean(usuario?.esAdmin) || 
    cargoUpper.includes("TALENTO HUMANO") || 
    cargoUpper.includes("RECURSOS HUMANOS") || 
    cargoUpper.includes("SUPER") || 
    cargoUpper.includes("GERENTE") || 
    cargoUpper.includes("DIRECTOR");

  // Estados del módulo
  const [desprendibles, setDesprendibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mensaje, setMensaje] = useState({ text: "", type: "" });
  const [vistaActual, setVistaActual] = useState("mis_desprendibles"); // 'mis_desprendibles' | 'excel' | 'manual' | 'general'
  const [desprendibleSeleccionado, setDesprendibleSeleccionado] = useState(null);

  // Estados para Carga Masiva desde Excel
  const [datosExcelPrevio, setDatosExcelPrevio] = useState([]);
  const [nombreArchivoExcel, setNombreArchivoExcel] = useState("");
  const [cargandoExcel, setCargandoExcel] = useState(false);

  // Estado para el formulario de carga manual
  const [nuevoDesprendible, setNuevoDesprendible] = useState({
    documento_empleado: "",
    periodo: "2026-08 - 1ra Quincena",
    sueldo_basico: "",
    auxilio_transporte: "",
    horas_extras: 0,
    bonificaciones: 0,
    deducciones_salud: 0,
    deducciones_pension: 0,
    otras_deducciones: 0,
    neto_pagar: "",
    observaciones: ""
  });

  // --------------------------------------------------
  // CARGAR DESPRENDIBLES DESDE EL BACKEND
  // --------------------------------------------------
  const cargarDesprendibles = useCallback(async () => {
    setLoading(true);
    try {
      const url = (esRRHH && vistaActual === "general")
        ? `${process.env.REACT_APP_API_URL}/api/nomina`
        : `${process.env.REACT_APP_API_URL}/api/nomina/${usuario?.documento}`;

      const res = await fetch(url);
      const data = await res.json();

      if (data.success) {
        setDesprendibles(data.desprendibles || []);
      } else {
        setMensaje({ text: "Error al consultar la nómina.", type: "error" });
      }
    } catch (err) {
      console.error(err);
      setMensaje({ text: "No se pudo conectar con el servidor backend.", type: "error" });
    } finally {
      setLoading(false);
    }
  }, [esRRHH, vistaActual, usuario?.documento]);

  useEffect(() => {
    cargarDesprendibles();
  }, [cargarDesprendibles]);

  // --------------------------------------------------
  // PROCESAR ARCHIVO EXCEL (.XLSX / .CSV)
  // --------------------------------------------------
  const handleLeerExcel = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setNombreArchivoExcel(file.name);
    setCargandoExcel(true);
    setMensaje({ text: "", type: "" });

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        
        // Convertir la primera hoja de Excel a un Array de Objetos JSON
        const rawData = XLSX.utils.sheet_to_json(ws);

        if (rawData.length === 0) {
          setMensaje({ text: "El archivo Excel está vacío.", type: "error" });
          setDatosExcelPrevio([]);
          setCargandoExcel(false);
          return;
        }

        // Mapeo flexible de columnas
        const formateados = rawData.map((row) => {
          const doc = String(row.documento || row.cedula || row.documento_empleado || row.NIT || "").trim();
          const periodo = String(row.periodo || "2026-08 - 1ra Quincena").trim();
          const basico = Number(row.sueldo_basico || row.basico || row.salario || 0);
          const auxTrans = Number(row.auxilio_transporte || row.auxilio || 0);
          const extras = Number(row.horas_extras || row.extras || 0);
          const bonif = Number(row.bonificaciones || row.bonos || 0);
          const salud = Number(row.deducciones_salud || row.salud || 0);
          const pension = Number(row.deducciones_pension || row.pension || 0);
          const otrasDeduc = Number(row.otras_deducciones || row.deducciones || 0);

          // Neto a pagar calculado automáticamente si no viene en el Excel
          const netoCalculado = (basico + auxTrans + extras + bonif) - (salud + pension + otrasDeduc);
          const netoFinal = row.neto_pagar ? Number(row.neto_pagar) : Math.max(0, netoCalculado);

          return {
            documento_empleado: doc,
            periodo: periodo,
            sueldo_basico: basico,
            auxilio_transporte: auxTrans,
            horas_extras: extras,
            bonificaciones: bonif,
            deducciones_salud: salud,
            deducciones_pension: pension,
            otras_deducciones: otrasDeduc,
            neto_pagar: netoFinal,
            observaciones: row.observaciones || "Carga Masiva Excel"
          };
        }).filter(item => item.documento_empleado !== "");

        setDatosExcelPrevio(formateados);
      } catch (error) {
        console.error("Error al leer el archivo Excel:", error);
        setMensaje({ text: "Ocurrió un error al procesar la estructura del Excel. Asegúrate de que sea un archivo válido.", type: "error" });
      } finally {
        setCargandoExcel(false);
      }
    };

    reader.readAsBinaryString(file);
  };

  // --------------------------------------------------
  // GUARDAR MASIVAMENTE EN LA BASE DE DATOS
  // --------------------------------------------------
  const handleConfirmarCargaMasiva = async () => {
    if (datosExcelPrevio.length === 0) {
      setMensaje({ text: "No hay registros listos para cargar.", type: "error" });
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/nomina`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(datosExcelPrevio)
      });
      const data = await res.json();

      if (data.success) {
        setMensaje({ text: `¡Éxito! Se han publicado ${datosExcelPrevio.length} desprendibles de nómina.`, type: "success" });
        setDatosExcelPrevio([]);
        setNombreArchivoExcel("");
        setVistaActual("general");
      } else {
        setMensaje({ text: data.message || "Error al publicar la nómina en la base de datos.", type: "error" });
      }
    } catch (err) {
      console.error(err);
      setMensaje({ text: "Error de conexión con el servidor backend.", type: "error" });
    }
  };

  // Autocalcular el Neto para la carga manual
  const handleInputChangeManual = (field, value) => {
    const copia = { ...nuevoDesprendible, [field]: value };
    const devengado = Number(copia.sueldo_basico || 0) + Number(copia.auxilio_transporte || 0) + Number(copia.horas_extras || 0) + Number(copia.bonificaciones || 0);
    const deducciones = Number(copia.deducciones_salud || 0) + Number(copia.deducciones_pension || 0) + Number(copia.otras_deducciones || 0);
    copia.neto_pagar = Math.max(0, devengado - deducciones);
    setNuevoDesprendible(copia);
  };

  const handleGuardarManual = async (e) => {
    e.preventDefault();
    if (!nuevoDesprendible.documento_empleado || !nuevoDesprendible.sueldo_basico) {
      setMensaje({ text: "Completa el documento del empleado y el sueldo básico.", type: "error" });
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/nomina`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(nuevoDesprendible)
      });
      const data = await res.json();

      if (data.success) {
        setMensaje({ text: "¡Desprendible individual guardado exitosamente!", type: "success" });
        setNuevoDesprendible({
          documento_empleado: "",
          periodo: "2026-08 - 1ra Quincena",
          sueldo_basico: "",
          auxilio_transporte: "",
          horas_extras: 0,
          bonificaciones: 0,
          deducciones_salud: 0,
          deducciones_pension: 0,
          otras_deducciones: 0,
          neto_pagar: "",
          observaciones: ""
        });
        setVistaActual("general");
      } else {
        setMensaje({ text: data.message || "Error al guardar.", type: "error" });
      }
    } catch (err) {
      console.error(err);
      setMensaje({ text: "Error de conexión.", type: "error" });
    }
  };

  const handleImprimir = () => {
    window.print();
  };

  return (
    <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl p-4 sm:p-8 max-w-5xl w-full mx-auto max-h-[92vh] overflow-y-auto border border-white/20">
      
      {/* CABECERA DE NÓMINA */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-gray-200 pb-4 mb-6 gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-800 flex items-center gap-2">
            💳 Módulo de Nómina y Pagos
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            Consulta, liquidación y descarga oficial de comprobantes de pago.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          {esRRHH && (
            <>
              {vistaActual !== "mis_desprendibles" && (
                <button
                  onClick={() => setVistaActual("mis_desprendibles")}
                  className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-xl text-xs font-bold transition border border-blue-200 shadow-sm cursor-pointer"
                >
                  Mis Pagos
                </button>
              )}
              {vistaActual !== "general" && (
                <button
                  onClick={() => setVistaActual("general")}
                  className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-xl text-xs font-bold transition border border-indigo-200 shadow-sm cursor-pointer"
                >
                  📋 Nómina General
                </button>
              )}
              {vistaActual !== "excel" && (
                <button
                  onClick={() => setVistaActual("excel")}
                  className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition shadow-sm cursor-pointer flex items-center gap-1"
                >
                  <span>📊</span> Cargar Excel
                </button>
              )}
            </>
          )}

          <button
            onClick={onBackToMenu}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold transition border border-gray-300 shadow-sm cursor-pointer"
          >
            Volver al Menú
          </button>
        </div>
      </div>

      {/* BANNER NOTIFICACIONES */}
      {mensaje.text && (
        <div className={`mb-6 p-4 rounded-xl text-xs sm:text-sm font-bold flex justify-between items-center ${
          mensaje.type === "success" ? "bg-green-100 text-green-800 border border-green-300" : "bg-red-100 text-red-800 border border-red-300"
        }`}>
          <span>{mensaje.text}</span>
          <button onClick={() => setMensaje({ text: "", type: "" })} className="text-xs cursor-pointer font-black">✕</button>
        </div>
      )}

      {/* VISTA 1 & 2: LISTA DE DESPRENDIBLES (MIS PAGOS O NÓMINA GENERAL) */}
      {(vistaActual === "mis_desprendibles" || vistaActual === "general") && (
        <div>
          {loading ? (
            <div className="text-center py-12">
              <span className="text-4xl animate-bounce inline-block mb-3">💵</span>
              <p className="text-xs text-gray-500 font-bold">Cargando comprobantes de nómina...</p>
            </div>
          ) : desprendibles.length === 0 ? (
            <div className="p-12 text-center bg-gray-50 rounded-2xl border border-gray-200">
              <span className="text-5xl block mb-3">📄</span>
              <h3 className="text-base font-bold text-gray-800">No hay desprendibles registrados</h3>
              <p className="text-xs text-gray-500 mt-1 mb-4">Aún no hay registros de nómina cargados en el sistema.</p>
              {esRRHH && (
                <button
                  onClick={() => setVistaActual("excel")}
                  className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow transition cursor-pointer"
                >
                  📊 Realizar Carga Masiva desde Excel
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {desprendibles.map((item) => (
                <div key={item.id} className="bg-white border border-slate-200 p-5 rounded-2xl shadow-sm hover:shadow-md transition flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-xs font-bold text-blue-700 bg-blue-50 px-3 py-1 rounded-lg border border-blue-100">
                        {item.periodo}
                      </span>
                      <span className="text-xs text-slate-400 font-medium">
                        {new Date(item.fecha_carga).toLocaleDateString()}
                      </span>
                    </div>

                    {vistaActual === "general" && (
                      <p className="text-xs font-bold text-slate-800 mt-2">
                        👤 {item.nombre_empleado || "Empleado"} - C.C. {item.documento_empleado}
                      </p>
                    )}

                    <div className="mt-4 pt-3 border-t border-slate-100 flex justify-between items-center">
                      <div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Neto Recibido</p>
                        <p className="text-lg font-black text-emerald-600">
                          ${Number(item.neto_pagar).toLocaleString("es-CO")}
                        </p>
                      </div>
                      <button
                        onClick={() => setDesprendibleSeleccionado(item)}
                        className="px-4 py-2 bg-slate-900 hover:bg-black text-white text-xs font-bold rounded-xl transition shadow cursor-pointer"
                      >
                        Ver Detalle 👁️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* VISTA 3: CARGA MASIVA POR EXCEL (SOLO RRHH / ADMIN) */}
      {vistaActual === "excel" && esRRHH && (
        <div className="max-w-4xl mx-auto bg-slate-50 p-6 sm:p-8 rounded-2xl border border-slate-200">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-600 pl-3">
              Carga Masiva de Nómina desde Excel
            </h3>
            <button
              onClick={() => setVistaActual("manual")}
              className="text-xs font-bold text-blue-600 hover:underline cursor-pointer"
            >
              ¿Prefieres registro individual manual?
            </button>
          </div>

          <p className="text-xs text-slate-600 mb-6">
            Selecciona el archivo Excel de la quincena o mes. El sistema leerá las filas y generará automáticamente los desprendibles individuales para cada colaborador.
          </p>

          {/* ÁREA DE CARGA DE ARCHIVO */}
          <div className="bg-white p-6 rounded-2xl border-2 border-dashed border-emerald-300 text-center mb-6">
            <span className="text-5xl block mb-2">📊</span>
            <p className="text-xs font-bold text-slate-700 mb-1">
              Selecciona tu archivo de Excel (.xlsx, .xls o .csv)
            </p>
            <p className="text-[11px] text-slate-400 mb-4">
              Las columnas reconocidas son: <strong>documento, periodo, sueldo_basico, auxilio_transporte, horas_extras, bonificaciones, deducciones_salud, deducciones_pension, neto_pagar</strong>
            </p>

            <input
              type="file"
              accept=".xlsx, .xls, .csv"
              onChange={handleLeerExcel}
              className="block w-full max-w-md mx-auto text-xs text-slate-500 file:mr-4 file:py-2.5 file:px-5 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-emerald-600 file:text-white hover:file:bg-emerald-700 cursor-pointer"
            />

            {nombreArchivoExcel && (
              <p className="text-xs font-bold text-emerald-700 mt-3">
                ✓ Archivo cargado: {nombreArchivoExcel} ({datosExcelPrevio.length} colaboradores detectados)
              </p>
            )}
          </div>

          {/* TABLA DE VISTA PREVIA */}
          {cargandoExcel ? (
            <div className="text-center py-8">
              <span className="text-3xl animate-spin inline-block mb-2">⏳</span>
              <p className="text-xs text-slate-500 font-bold">Procesando datos del Excel...</p>
            </div>
          ) : datosExcelPrevio.length > 0 && (
            <div>
              <h4 className="text-xs font-bold text-slate-800 mb-2">Vista Previa de la Nómina a Publicar:</h4>
              <div className="overflow-x-auto border border-slate-200 rounded-xl mb-6 bg-white max-h-60 overflow-y-auto">
                <table className="w-full text-[11px] text-left">
                  <thead className="bg-slate-100 text-slate-700 font-bold border-b sticky top-0">
                    <tr>
                      <th className="p-2">Documento</th>
                      <th className="p-2">Periodo</th>
                      <th className="p-2">Sueldo Básico</th>
                      <th className="p-2">Aux. Trans.</th>
                      <th className="p-2">Extras/Bonif.</th>
                      <th className="p-2">Deducciones</th>
                      <th className="p-2">Neto a Pagar</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {datosExcelPrevio.map((row, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="p-2 font-bold">{row.documento_empleado}</td>
                        <td className="p-2">{row.periodo}</td>
                        <td className="p-2">${row.sueldo_basico.toLocaleString("es-CO")}</td>
                        <td className="p-2">${row.auxilio_transporte.toLocaleString("es-CO")}</td>
                        <td className="p-2">${(row.horas_extras + row.bonificaciones).toLocaleString("es-CO")}</td>
                        <td className="p-2 text-red-600">-${(row.deducciones_salud + row.deducciones_pension + row.otras_deducciones).toLocaleString("es-CO")}</td>
                        <td className="p-2 font-bold text-emerald-600">${row.neto_pagar.toLocaleString("es-CO")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => { setDatosExcelPrevio([]); setNombreArchivoExcel(""); }}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold text-xs rounded-xl transition cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmarCargaMasiva}
                  className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-lg transition cursor-pointer"
                >
                  Publicar {datosExcelPrevio.length} Desprendibles Ahora 🚀
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* VISTA 4: CARGA MANUAL INDIVIDUAL */}
      {vistaActual === "manual" && esRRHH && (
        <div className="max-w-2xl mx-auto bg-slate-50 p-6 sm:p-8 rounded-2xl border border-slate-200">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-600 pl-3">
              Registrar Desprendible Individual
            </h3>
            <button
              onClick={() => setVistaActual("excel")}
              className="text-xs font-bold text-emerald-600 hover:underline cursor-pointer"
            >
              ← Volver a Carga por Excel
            </button>
          </div>

          <form onSubmit={handleGuardarManual} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Cédula del Empleado *</label>
                <input
                  type="text"
                  required
                  value={nuevoDesprendible.documento_empleado}
                  onChange={(e) => handleInputChangeManual("documento_empleado", e.target.value)}
                  className="w-full px-4 py-2 rounded-xl border border-slate-300 text-sm bg-white"
                  placeholder="Número de documento"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Periodo de Pago *</label>
                <input
                  type="text"
                  required
                  value={nuevoDesprendible.periodo}
                  onChange={(e) => handleInputChangeManual("periodo", e.target.value)}
                  className="w-full px-4 py-2 rounded-xl border border-slate-300 text-sm bg-white"
                  placeholder="Ej: 2026-08 - 1ra Quincena"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Sueldo Básico ($) *</label>
                <input
                  type="number"
                  required
                  value={nuevoDesprendible.sueldo_basico}
                  onChange={(e) => handleInputChangeManual("sueldo_basico", e.target.value)}
                  className="w-full px-4 py-2 rounded-xl border border-slate-300 text-sm bg-white"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Auxilio de Transporte ($)</label>
                <input
                  type="number"
                  value={nuevoDesprendible.auxilio_transporte}
                  onChange={(e) => handleInputChangeManual("auxilio_transporte", e.target.value)}
                  className="w-full px-4 py-2 rounded-xl border border-slate-300 text-sm bg-white"
                  placeholder="0"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Horas Extras ($)</label>
                <input
                  type="number"
                  value={nuevoDesprendible.horas_extras}
                  onChange={(e) => handleInputChangeManual("horas_extras", e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-slate-300 text-xs bg-white"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Bonificaciones ($)</label>
                <input
                  type="number"
                  value={nuevoDesprendible.bonificaciones}
                  onChange={(e) => handleInputChangeManual("bonificaciones", e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-slate-300 text-xs bg-white"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Deducción Salud ($)</label>
                <input
                  type="number"
                  value={nuevoDesprendible.deducciones_salud}
                  onChange={(e) => handleInputChangeManual("deducciones_salud", e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-slate-300 text-xs bg-white"
                />
              </div>
            </div>

            <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 flex justify-between items-center">
              <span className="text-xs font-bold text-emerald-900">Total Neto a Pagar:</span>
              <span className="text-xl font-black text-emerald-700">
                ${Number(nuevoDesprendible.neto_pagar || 0).toLocaleString("es-CO")}
              </span>
            </div>

            <div className="flex justify-end pt-3">
              <button
                type="submit"
                className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold shadow-lg transition cursor-pointer"
              >
                Guardar Desprendible 💾
              </button>
            </div>
          </form>
        </div>
      )}

      {/* MODAL DETALLE DE DESPRENDIBLE DIGITAL */}
      {desprendibleSeleccionado && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-xl w-full p-6 sm:p-8 border border-slate-200 max-h-[90vh] overflow-y-auto">
            
            <div className="text-center border-b border-slate-200 pb-4 mb-4">
              <h3 className="text-lg font-black text-slate-800">EL GRAN SURTIDOR</h3>
              <p className="text-xs text-slate-500 font-bold">Comprobante Oficial de Pago de Nómina</p>
              <span className="inline-block mt-2 text-xs font-bold text-blue-700 bg-blue-50 px-3 py-1 rounded-full border border-blue-200">
                Periodo: {desprendibleSeleccionado.periodo}
              </span>
            </div>

            <div className="space-y-3 text-xs text-slate-700 mb-6">
              <div className="flex justify-between py-1 border-b border-slate-100">
                <span className="font-bold text-slate-500">Documento Colaborador:</span>
                <span className="font-bold">{desprendibleSeleccionado.documento_empleado}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-100">
                <span>Sueldo Básico:</span>
                <span>${Number(desprendibleSeleccionado.sueldo_basico).toLocaleString("es-CO")}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-100">
                <span>Auxilio de Transporte:</span>
                <span>${Number(desprendibleSeleccionado.auxilio_transporte).toLocaleString("es-CO")}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-100">
                <span>Horas Extras / Recargos:</span>
                <span>${Number(desprendibleSeleccionado.horas_extras).toLocaleString("es-CO")}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-100 text-red-600">
                <span>Deducciones (Salud / Pensión):</span>
                <span>-${(Number(desprendibleSeleccionado.deducciones_salud) + Number(desprendibleSeleccionado.deducciones_pension) + Number(desprendibleSeleccionado.otras_deducciones)).toLocaleString("es-CO")}</span>
              </div>
              <div className="flex justify-between py-2 border-t-2 border-slate-800 font-black text-sm text-slate-900">
                <span>TOTAL NETO RECIBIDO:</span>
                <span className="text-emerald-600">${Number(desprendibleSeleccionado.neto_pagar).toLocaleString("es-CO")}</span>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={handleImprimir}
                className="px-4 py-2 bg-blue-50 text-blue-700 font-bold rounded-xl text-xs hover:bg-blue-100 transition cursor-pointer"
              >
                🖨️ Imprimir Comprobante
              </button>
              <button
                onClick={() => setDesprendibleSeleccionado(null)}
                className="px-5 py-2 bg-slate-800 text-white font-bold rounded-xl text-xs hover:bg-black transition cursor-pointer"
              >
                Cerrar
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}